import { useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/auth-context";
import { AppRoutes } from "../app/routes";
import { useScrollResetOnNavigate } from "../app/use-scroll-reset-on-navigate";
import "./backoffice-shell.css";

const initials = (name?: string) => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "·";
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/** The nav items each backoffice role sees. Only screens that exist are listed;
 *  more land as their slices ship. */
const NAV_BY_ROLE: Record<string, { to: string; icon: string; labelKey: string }[]> = {
  supervisor: [
    { to: AppRoutes.SUPERVISOR_SUMMARY, icon: "dashboard", labelKey: "nav_summary" },
    { to: AppRoutes.ALL_DELIVERIES, icon: "list_alt", labelKey: "nav_all_deliveries" },
    { to: AppRoutes.PENDING_CONFIRMATION, icon: "phone_in_talk", labelKey: "nav_pending_confirmation" },
    { to: AppRoutes.SUPERVISOR_ROUTE_ASSIGNMENT, icon: "route", labelKey: "nav_assignment" },
    { to: AppRoutes.UNDELIVERED, icon: "event_busy", labelKey: "nav_undelivered" },
    { to: AppRoutes.SUPERVISOR_REPORTS, icon: "flag", labelKey: "nav_reports" },
    { to: AppRoutes.SUPERVISOR_RESCHEDULES, icon: "event_repeat", labelKey: "nav_reschedules" },
    { to: AppRoutes.SUPERVISOR_TEAM, icon: "group", labelKey: "nav_team" },
    { to: AppRoutes.REFERRALS_BACKOFFICE, icon: "person_add", labelKey: "nav_referrals" },
  ],
  contact_center: [
    { to: AppRoutes.PENDING_CONFIRMATION, icon: "phone_in_talk", labelKey: "nav_pending_confirmation" },
    { to: AppRoutes.UNDELIVERED, icon: "event_busy", labelKey: "nav_undelivered" },
  ],
  admin: [
    { to: AppRoutes.ADMIN_USERS, icon: "group", labelKey: "nav_users" },
    { to: AppRoutes.ALL_DELIVERIES, icon: "list_alt", labelKey: "nav_all_deliveries" },
    { to: AppRoutes.ADMIN_ZONES, icon: "map", labelKey: "nav_zones" },
    { to: AppRoutes.UNDELIVERED, icon: "event_busy", labelKey: "nav_undelivered" },
    { to: AppRoutes.REFERRALS_BACKOFFICE, icon: "person_add", labelKey: "nav_referrals" },
    { to: AppRoutes.COMPLIANCE_CUSTOMERS, icon: "group", labelKey: "nav_compliance" },
  ],
  operations: [
    { to: AppRoutes.OPERATIONS_BATCHES, icon: "inventory_2", labelKey: "nav_batches" },
    { to: AppRoutes.OPERATIONS_INVENTORY, icon: "credit_card", labelKey: "nav_inventory" },
    { to: AppRoutes.OPERATIONS_DISTRIBUTION, icon: "local_shipping", labelKey: "nav_distribution" },
    { to: AppRoutes.OPERATIONS_DAY_CLOSE, icon: "event_available", labelKey: "nav_day_close" },
    { to: AppRoutes.UNDELIVERED, icon: "event_busy", labelKey: "nav_undelivered" },
    { to: AppRoutes.OPERATIONS_CUSTODY, icon: "folder_supervised", labelKey: "nav_custody" },
    { to: AppRoutes.COMPLIANCE_CUSTOMERS, icon: "group", labelKey: "nav_compliance" },
  ],
  compliance: [
    { to: AppRoutes.COMPLIANCE_CUSTOMERS, icon: "group", labelKey: "nav_compliance" },
  ],
};

/**
 * The backoffice app-shell: the desktop chrome (left sidebar + content) that
 * wraps the supervisor/admin/operations screens. Its own lazy chunk, so field
 * (ambassador) users never download it (D-5 / E0.3). The nav + the account
 * role label are driven by the signed-in user's role.
 */
export const BackofficeShell = () => {
  const { t } = useTranslation("backoffice");
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef<HTMLElement>(null);
  useScrollResetOnNavigate(contentRef);

  const navItems = NAV_BY_ROLE[user?.role ?? ""] ?? [];
  const roleLabel = user?.role ? t(`role_${user.role}`) : "";

  const handleSignOut = async () => {
    await signOut();
    navigate(AppRoutes.LOGIN, { replace: true });
  };

  const navLinks = (onClick?: () => void) =>
    navItems.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={onClick}
        className={({ isActive }) => `bo-nav-item${isActive ? " bo-nav-item-on" : ""}`}
      >
        <span className="ms bo-nav-icon" aria-hidden="true">
          {item.icon}
        </span>
        {t(item.labelKey)}
      </NavLink>
    ));

  const account = (
    <div className="bo-account">
      <button
        type="button"
        className="bo-account-button"
        aria-label={t("account")}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <span className="bo-avatar">{initials(user?.name)}</span>
        <span className="bo-account-meta">
          <span className="bo-account-name">{user?.name}</span>
          <span className="bo-account-role">{roleLabel}</span>
        </span>
      </button>
      {menuOpen && (
        <div className="bo-menu" role="menu">
          <span className="bo-menu-email">{user?.email}</span>
          <div className="bo-menu-divider" />
          <button type="button" className="bo-menu-action" role="menuitem" onClick={handleSignOut}>
            {t("sign_out")}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="bo-shell">
      <aside className="bo-sidebar">
        <div className="bo-sidebar-top">
          <div className="bo-brand">
            <img className="bo-brand-logo" src="/vanacard-logo.svg" alt="VanaCard" />
          </div>
          <nav className="bo-nav">{navLinks()}</nav>
        </div>
        {account}
      </aside>

      <div className="bo-main">
        {/* Mobile top bar: hamburger opens the nav drawer. */}
        <div className="bo-topbar">
          <button
            type="button"
            className="bo-topbar-menu"
            aria-label={t("menu_open")}
            onClick={() => setNavOpen(true)}
          >
            <span className="ms" aria-hidden="true">
              menu
            </span>
          </button>
          <img className="bo-topbar-logo" src="/vanacard-logo.svg" alt="VanaCard" />
          <span className="bo-avatar bo-topbar-avatar">{initials(user?.name)}</span>
        </div>

        <main className="bo-content" ref={contentRef}>
          <Outlet />
        </main>
      </div>

      {/* Mobile nav drawer (slides in from the left). */}
      {navOpen && (
        <div className="bo-drawer-scrim" onClick={() => setNavOpen(false)}>
          <div className="bo-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="bo-drawer-top">
              <div className="bo-drawer-head">
                <img className="bo-brand-logo" src="/vanacard-logo.svg" alt="VanaCard" />
                <button
                  type="button"
                  className="bo-drawer-close"
                  aria-label={t("menu_close")}
                  onClick={() => setNavOpen(false)}
                >
                  <span className="ms" aria-hidden="true">
                    close
                  </span>
                </button>
              </div>
              <nav className="bo-nav">{navLinks(() => setNavOpen(false))}</nav>
            </div>
            <div className="bo-drawer-account">
              <span className="bo-avatar">{initials(user?.name)}</span>
              <span className="bo-account-meta">
                <span className="bo-account-name">{user?.name}</span>
                <span className="bo-account-role">{roleLabel}</span>
              </span>
              <button
                type="button"
                className="bo-drawer-signout"
                aria-label={t("sign_out")}
                onClick={handleSignOut}
              >
                <span className="ms" aria-hidden="true">
                  logout
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
